import { observer } from "mobx-react";
import Scaffold from "../../../../components/Scaffold";
import store from "../../../../stores/promosStore";
import { useEffect } from "react";
import { Button, DatePicker, Drawer, Flex, Form, Input, InputNumber, Select, Table, Tag } from "antd";
import DropdownActions from "../../../../components/DropdownActions";
import Utils from "../../../../utils/Utils";
import decimalEntry from "../../../../utils/decimalEntry";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const AdminPromosView = observer(() => {
  const [form] = Form.useForm();

  useEffect(() => {
    store.findAll();
    return () => {
      store.data = undefined;
    };
  }, []);

  const onCreate = () => {
    form.resetFields();
    const defaultStartDate = dayjs();
    const defaultEndDate = dayjs().add(30, 'days');
    
    form.setFieldsValue({
      type: 'percent',
      amount: 0,
      dateRange: [defaultStartDate, defaultEndDate],
    });
    store.state = "Create";
  };

  const onEdit = async (id: string) => {
    await store.findOne(id);
    const formValues = {
      ...store.item,
      type: store.item?.type.value,
      dateRange: [
        store.item?.startsAt ? dayjs(store.item.startsAt) : dayjs(),
        store.item?.expiresAt ? dayjs(store.item.expiresAt) : dayjs().add(30, 'days'),
      ],
    };
    form.setFieldsValue(formValues);
    store.state = "Edit";
  };

  const onCancel = () => {
    form.resetFields();
    store.state = "None";
  };

  const onRemove = async (id: string) => {
    await store.onRemove(id);
  };

  const onCodeGenerate = () => {
    const code = store.generatePromoCode();
    form.setFieldValue("code", code);
  };

  const onConfirm = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      
      if (values.dateRange && values.dateRange.length === 2) {
        const [startDate, endDate] = values.dateRange;
        
        values.startsAt = startDate.startOf('day').toISOString();
        
        values.expiresAt = endDate.endOf('day').toISOString();
        
        delete values.dateRange;
      }
      
      if (store.state === "Create") {
        await store.onCreate(values);
      }

      if (store.state === "Edit") {
        await store.onUpdate(values);
      }

      await store.findAll();
      onCancel();
    } catch (error) {
      console.error(error);
    }
  };

  const formatAmount = (amount: number, type: string) => {
    if (type === 'percent') {
      return `${amount}%`;
    }
    return `${amount} ₽`;
  };

  const validateDateRange = (_: any, value: any) => {
    if (!value || value.length !== 2) {
      return Promise.reject(new Error('Укажите период действия промокода'));
    }
    
    const [start, end] = value;
    const now = dayjs().startOf('day');

    
    if (end.isBefore(start)) {
      return Promise.reject(new Error('Дата окончания должна быть позже даты начала'));
    }
    
    return Promise.resolve();
  };

  return (
    <div style={{ backgroundColor: "white" }}>
      <Scaffold
        title="Промокоды"
        trailing={
          <Flex gap={12}>
            <Button onClick={onCreate} type="primary">
              Добавить
            </Button>
            <Input 
              style={{ width: 220 }} 
              placeholder="Поиск по названию, коду или описанию" 
              value={store.search} 
              onChange={(e) => (store.search = e.target.value)} 
            />
          </Flex>
        }
      >
        <div style={{ height: 12 }} />
        <Table
          dataSource={store.filteredResults}
          rowKey={(e) => e.id}
          pagination={false}
          style={{ borderTop: "1px solid var(--night100)" }}
          loading={!store.data}
          scroll={{ y: "calc(100vh - 205px)" }}
          columns={[
            {
              title: "Название",
              dataIndex: "name",
              width: 200,
            },
            {
              title: "Код",
              dataIndex: "code",
              align: "center",
              width: 150,
            },
            {
              title: "Описание",
              dataIndex: "description",
              ellipsis: true,
            },
            {
              title: "Скидка",
              align: "center",
              width: 120,
              render: (_, i) => formatAmount(i.amount, i.type.value),
            },
            {
              title: "Тип",
              align: "center",
              width: 150,
              render: (_, i) => i.type.title,
            },
            {
              title: "Период действия",
              width: 250,
              align: "center",
              render: (_, i) => {
                const start = i.startsAt ? Utils.format(i.startsAt, "DD.MM.YYYY") : "Сейчас";
                const end = i.expiresAt ? Utils.format(i.expiresAt, "DD.MM.YYYY") : "Без срока";
                return `${start} - ${end}`;
              },
            },
            {
              title: "Статус",
              align: "center",
              width: 140,
              render: (_, i) => {
                const status = store.getPromoStatusByItem(i);
                return (
                  <Tag color={status.color} style={{ margin: 0 }}>
                    {status.text}
                  </Tag>
                );
              },
            },
            {
              title: "Дата создания",
              width: 150,
              align: "center",
              render: (_, i) => Utils.format(i.createdAt, "DD.MM.YYYY"),
            },
            {
              title: " ",
              width: 70,
              fixed: "right",
              render: (_, i) => (
                <DropdownActions
                  onEdit={async () => {
                    onEdit(i.id);
                  }}
                  onDelete={async () => onRemove(i.id)}
                />
              ),
            },
          ]}
        />
        <Drawer
          title={store.state === "Create" ? "Добавить промокод" : "Редактировать промокод"}
          open={store.state !== "None"}
          width={720}
          onClose={onCancel}
          footer={
            <Flex justify="flex-start" gap={12}>
              <Button disabled={store.isLoading} type="primary" onClick={onConfirm}>
                Сохранить
              </Button>
              <Button disabled={store.isLoading} onClick={onCancel}>
                Отменить
              </Button>
            </Flex>
          }
        >
          <Form layout="vertical" form={form}>
            <Form.Item 
              name="name" 
              label="Название" 
              rules={[{ required: true, message: "Введите название промокода" }]}
            >
              <Input placeholder="Введите название для промокода" />
            </Form.Item>
            
            <Form.Item 
              name="code" 
              label="Промокод" 
              rules={[{ required: true, message: "Введите код промокода" }]}
            >
              <Input placeholder="P4RQ3D8X" />
            </Form.Item>
            
            <Button style={{ marginBottom: 16 }} onClick={onCodeGenerate}>
              Генерация кода
            </Button>
            
            <Form.Item name="description" label="Описание">
              <Input.TextArea 
                placeholder="Введите описание промокода" 
                rows={3}
              />
            </Form.Item>
            
            <Form.Item 
              name="type" 
              label="Тип скидки" 
              rules={[{ required: true, message: "Выберите тип скидки" }]}
            >
              <Select
                allowClear={false}
                options={[
                  { value: "percent", label: "Процент" },
                  { value: "fixed", label: "Фиксированная сумма" },
                ]}
              />
            </Form.Item>
            
            <Form.Item 
              name="amount" 
              label="Сумма скидки" 
              rules={[
                { required: true, message: "Введите сумму скидки" },
                { 
                  validator: (_, value) => {
                    if (form.getFieldValue("type") === "percent" && value > 100) {
                      return Promise.reject(new Error('Процент скидки не может превышать 100%'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Введите сумму скидки"
                min={0}
                step={0.01}
                precision={form.getFieldValue("type") === "percent" ? 0 : 2}
                onKeyDown={decimalEntry}
              />
            </Form.Item>
            
            <Form.Item 
              name="dateRange" 
              label="Период действия"
              rules={[
                { required: true, message: "Укажите период действия промокода" },
                { validator: validateDateRange }
              ]}
              tooltip="Промокод будет активен с 00:00 даты начала по 23:59 даты окончания"
            >
              <RangePicker
                style={{ width: "100%" }}
                placeholder={["Дата начала", "Дата окончания"]}
                format="DD.MM.YYYY"
              />
            </Form.Item>
          </Form>
        </Drawer>
      </Scaffold>
    </div>
  );
});

export default AdminPromosView;